import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Pagination } from '@/components/molecules';
import { Modal } from '@/components/molecules/Modal';
import { ReportFilters, ReportSummaryCards, ReportTable } from '@/components/organisms/Reports';
import type { TableColumn } from '@/components/organisms/Reports';
import { useVendorsReport, useExportReportMutation } from '@/hooks/useErpApi';
import type { VendorReportFilter, VendorReportRecord } from '@kisanlink/erp-service';

export default function VendorsReportPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<VendorReportFilter>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedRecord, setSelectedRecord] = useState<VendorReportRecord | null>(null);
  
  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useVendorsReport({ ...filters, limit: itemsPerPage, offset });
  const { mutateAsync: exportReport, isLoading: isExporting } = useExportReportMutation();

  const handleFilterChange = useCallback((newFilters: Record<string, unknown>) => {
    setFilters(newFilters as VendorReportFilter);
    setCurrentPage(1);
  }, []);
  
  // Get total count from API response if available
  const totalItems = report?.pagination?.total || (report?.records?.length || 0);

  const handleExport = useCallback(
    async (format: 'xlsx' | 'pdf') => {
      try {
        const blob = await exportReport({ reportType: 'vendors', filters: { ...filters, format } });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vendors-report.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Export failed:', err);
      }
    },
    [filters, exportReport]
  );

  const columns: TableColumn<VendorReportRecord>[] = [
    {
      key: 'company_name',
      header: 'Company',
      render: (row) => (
        <div>
          <div className="font-medium text-neutral-900">{row.company_name}</div>
          {row.contact_person && (
            <div className="text-xs text-neutral-500">{row.contact_person}</div>
          )}
        </div>
      ),
    },
    {
      key: 'contact_number',
      header: 'Contact',
      render: (row) => (
        <div className="text-sm">
          {row.contact_number || '-'}
          {row.email && <div className="text-xs text-neutral-500">{row.email}</div>}
        </div>
      ),
    },
    {
      key: 'gst_number',
      header: 'GST',
      className: 'min-w-[140px]',
      render: (row) =>
        row.gst_number ? (
          <span className="font-mono text-xs text-neutral-600 whitespace-nowrap">{row.gst_number}</span>
        ) : (
          <Badge variant="warning" size="sm">
            No GST
          </Badge>
        ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.is_active ? 'success' : 'error'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'product_count',
      header: 'Products',
      render: (row) => row.product_count.toLocaleString(),
    },
    {
      key: 'total_po_value',
      header: 'PO Value',
      render: (row) => (
        <span className="font-medium text-neutral-900">₹{row.total_po_value.toLocaleString('en-IN')}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedRecord(row)}
          className="text-neutral-600 hover:text-primary-600"
        >
          <Icon name="Eye" size={16} />
        </Button>
      ),
    },
  ];

  const summary = report?.summary;
  const summaryCards = summary
    ? [
        {
          label: 'Total Vendors',
          value: summary.total_vendors,
          icon: 'Users' as const,
          color: 'primary' as const,
          format: 'number' as const,
        },
        {
          label: 'Active Vendors',
          value: summary.active_vendors,
          icon: 'UserCheck' as const,
          color: 'success' as const,
          format: 'number' as const,
        },
        {
          label: 'GST Registered',
          value: summary.vendors_with_gst,
          icon: 'FileCheck' as const,
          color: 'info' as const,
          format: 'number' as const,
        },
        {
          label: 'Total PO Value',
          value: summary.total_po_value,
          icon: 'IndianRupee' as const,
          color: 'warning' as const,
          format: 'currency' as const,
        },
      ]
    : [];


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <Icon name="ArrowLeft" size={16} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Vendors Report</h1>
            <p className="text-neutral-600 text-sm mt-1">
              Vendor information with contact details and purchase values
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <ReportFilters
        showDateRange={false}
        showSearch={true}
        searchPlaceholder="Search vendors..."
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {/* Summary Cards */}
      {!isLoading && !error && summary && <ReportSummaryCards cards={summaryCards} />}

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <ReportTable
          columns={columns}
          data={report?.records || []}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          emptyMessage="No vendors found matching your criteria"
        />
        {!isLoading && !error && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            disabled={isLoading}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedRecord}
        onClose={() => setSelectedRecord(null)}
        title="Vendor Details"
        size="lg"
      >
        {selectedRecord && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-neutral-500 mb-1">Vendor ID</p>
                <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.id}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-500 mb-1">Company Name</p>
                <p className="text-sm font-medium text-neutral-900">{selectedRecord.company_name}</p>
              </div>
              {selectedRecord.contact_person && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Contact Person</p>
                  <p className="text-sm text-neutral-900">{selectedRecord.contact_person}</p>
                </div>
              )}
              {selectedRecord.contact_number && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Contact Number</p>
                  <p className="text-sm text-neutral-900">{selectedRecord.contact_number}</p>
                </div>
              )}
              {selectedRecord.email && (
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Email</p>
                  <p className="text-sm text-neutral-900">{selectedRecord.email}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-neutral-500 mb-1">Status</p>
                <Badge variant={selectedRecord.is_active ? 'success' : 'error'}>
                  {selectedRecord.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Tax Information</h3>
              <div className="grid grid-cols-2 gap-4">
                {selectedRecord.gst_number && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">GST Number</p>
                    <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.gst_number}</p>
                  </div>
                )}
                {selectedRecord.pan_number && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-1">PAN Number</p>
                    <p className="text-sm font-medium text-neutral-900 font-mono">{selectedRecord.pan_number}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <h3 className="text-sm font-semibold text-neutral-700 mb-3">Business Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Product Count</p>
                  <p className="text-sm font-medium text-neutral-900">{selectedRecord.product_count.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Total PO Value</p>
                  <p className="text-sm font-medium text-neutral-900">₹{selectedRecord.total_po_value.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Created At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Updated At</p>
                  <p className="text-sm text-neutral-900">{new Date(selectedRecord.updated_at).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
